import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { Drawer } from "./Drawer";
import { PreviewBanner } from "./PreviewBanner";
import { TabNav } from "./TabNav";
import { useAuth } from "../auth/AuthProvider";
import { useTheme } from "../hooks/useTheme";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { usePendingCount } from "../hooks/usePendingCount";
import { getSuggestionForUser } from "../lib/greetingSuggestions";
import { useFeature } from "../hooks/useFeature";
import { useCurrentHeaderSearch } from "../lib/headerSearch";

function HamburgerIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function SearchIcon({ size = 22 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function AppHeader() {
  const { state } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const pageSearch = useCurrentHeaderSearch();
  const meQuery = useMe();
  const membership = resolveMembership(meQuery.data);
  const profileIncomplete = meQuery.data?.user.needsOnboarding ?? false;
  const displayName =
    state.status === "signed-in"
      ? state.user.displayName ?? state.user.phoneNumber ?? state.user.email
      : null;
  const familyName = membership?.familyName;
  // Picked once per sign-in (cached in localStorage) so it sticks
  // across reloads but rolls over for the next session.
  const userId = meQuery.data?.user.id ?? null;
  // Drop AI suggestions for anyone without the beta feature — the prompt
  // fires a window event that nothing is listening for otherwise.
  const aiEnabled = useFeature("ai_chat");
  const suggestion = getSuggestionForUser(userId, aiEnabled === true);
  const pendingCount = usePendingCount();

  // The family name in the mobile header gets hidden when there isn't
  // room to show it in full. We measure scrollWidth vs clientWidth and
  // re-measure on resize so a long name like "Behailay" doesn't show
  // up as a half-truncated "Behaila…".
  const familyNameRef = useRef<HTMLSpanElement>(null);
  const [familyNameTruncated, setFamilyNameTruncated] = useState(false);

  useEffect(() => {
    const el = familyNameRef.current;
    if (!el || !familyName) return;
    const check = () => {
      // Run after the next paint so layout is settled.
      requestAnimationFrame(() => {
        if (!familyNameRef.current) return;
        const node = familyNameRef.current;
        // Reset visibility first so we can take an honest measurement.
        node.style.visibility = "hidden";
        node.style.display = "block";
        const overflowing = node.scrollWidth > node.clientWidth + 1;
        node.style.visibility = "";
        node.style.display = "";
        setFamilyNameTruncated(overflowing);
      });
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener("resize", check);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [familyName]);

  return (
    <>
      <PreviewBanner />
      <header className="app-header">
        <div className="app-header__inner">
          {/* Left: Logo + family name (mobile shows name next to logo) */}
          <div className="app-header__left">
            <Logo variant={theme === "dark" ? "on-dark" : "on-light"} size={56} />
            {familyName && (
              <span
                ref={familyNameRef}
                className={`app-header__family-name ${familyNameTruncated ? "is-hidden" : ""}`}
              >
                {familyName}
              </span>
            )}
          </div>

          {/* Right: desktop shows user name + theme + hamburger;
           *  mobile shows [+] 🔍 ☰ */}
          <div className="app-header__right">
            {/* Desktop-only: greeting + name + suggestion subtitle */}
            {displayName && (
              <div
                className="app-header__user"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  lineHeight: 1.25,
                }}
              >
                <Link
                  to="/profile"
                  title={
                    profileIncomplete
                      ? "Complete your profile"
                      : "Edit your profile"
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <span style={{ fontWeight: 400 }}>
                    {/* Amharic ሰላም (Selam — peace, hello). Works for both
                     *  Amharic and Tigrinya speakers. Bundled webfont in
                     *  /public covers it identically across browsers. */}
                    <span
                      style={{
                        fontFamily:
                          '"Noto Ethiopic A", "Noto Sans Ethiopic", "Nyala", "Kefa", system-ui, serif',
                        marginRight: 6,
                        fontWeight: 400,
                      }}
                    >
                      ሰላም
                    </span>
                    <span style={{ fontWeight: 700 }}>{displayName}</span>
                  </span>
                  {profileIncomplete && (
                    <span
                      aria-label="Profile incomplete — add your name, location and bio"
                      title="Profile incomplete — add your name, location and bio"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "var(--c-danger, #dc2626)",
                        color: "var(--c-bg, #fff)",
                        fontSize: "0.55rem",
                        fontWeight: 600,
                        padding: "3px 7px",
                        borderRadius: "var(--r-pill, 999px)",
                        whiteSpace: "nowrap",
                        lineHeight: 1,
                      }}
                    >
                      Complete profile
                    </span>
                  )}
                </Link>
                {suggestion && (
                  <Link
                    to={suggestion.to}
                    className="app-header__suggestion"
                    onClick={() => {
                      // AI chat / composer aren't routes — open them via a
                      // window event once the destination has navigated.
                      const ev = suggestion.event;
                      if (ev) {
                        setTimeout(
                          () => window.dispatchEvent(new CustomEvent(ev)),
                          0,
                        );
                      }
                    }}
                    style={{
                      fontSize: "0.78rem",
                      color: "var(--c-text-muted)",
                      textDecoration: "none",
                      maxWidth: 480,
                      // Allow wrap to a second line for long prompts; the
                      // header already accommodates two lines via the
                      // greeting + suggestion stack, so a wrapped
                      // suggestion just stays inside the same column.
                      whiteSpace: "normal",
                      lineHeight: 1.3,
                      textAlign: "right",
                    }}
                  >
                    {suggestion.text}
                  </Link>
                )}
              </div>
            )}

            {/* Mobile-only: search icon */}
            <button
              className="app-header__icon-btn app-header__mobile-only"
              onClick={() => {
                // Search what this page is about (see lib/headerSearch).
                // Pages without a search box fall back to the Home post
                // search, which opens itself when it sees ?search=1.
                if (pageSearch) {
                  pageSearch.focus();
                } else {
                  navigate("/?search=1");
                }
              }}
              aria-label={`Search ${pageSearch?.label ?? "posts"}`}
              type="button"
              title={`Search ${pageSearch?.label ?? "posts"}`}
            >
              <SearchIcon />
            </button>

            {/* Desktop-only: theme toggle */}
            <button
              className="app-header__icon-btn app-header__desktop-only"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              type="button"
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>

            {/* Always: hamburger (with a badge when members await approval) */}
            <button
              className="app-header__icon-btn app-header__menu-btn"
              onClick={() => setDrawerOpen(true)}
              aria-label={
                pendingCount > 0
                  ? `Open menu — ${pendingCount} pending member${pendingCount === 1 ? "" : "s"} to approve`
                  : "Open menu"
              }
              type="button"
            >
              <HamburgerIcon />
              {pendingCount > 0 && (
                <span className="app-header__menu-badge" aria-hidden="true">
                  {pendingCount > 9 ? "9+" : pendingCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <TabNav />

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
